/**
 * Pure utility functions for Calendar date calculations.
 * No React dependencies — used by Calendar, CalendarMonthView, CalendarWeekView, etc.
 * Uses date-fns for all date arithmetic.
 */
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  addWeeks,
  addDays,
  isSameDay,
  isSameMonth,
  getISOWeek,
  parseISO,
  differenceInCalendarDays,
  isValid,
} from 'date-fns';

export type CalendarViewMode = 'month' | 'week' | 'day' | 'agenda';

export interface MonthGridDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  weekNumber: number;
  isWeekend: boolean;
}

export interface TimeSlot {
  hour: number;
  minutes: number;
  label: string;
  isBusinessHour: boolean;
}

/**
 * Generate a 5-6 row × 7 column grid of days for a month view.
 * Includes leading/trailing days from adjacent months.
 */
export function getMonthGrid(date: Date, firstDayOfWeek: number): MonthGridDay[][] {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get the start of the first week that includes the first day of the month
  const gridStart = startOfWeek(monthStart, { weekStartsOn: firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
  // Get the end of the last week that includes the last day of the month
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6 });

  const allDays = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const grid: MonthGridDay[][] = [];
  let currentWeek: MonthGridDay[] = [];

  for (const day of allDays) {
    const dayOfWeek = day.getDay();
    currentWeek.push({
      date: new Date(day),
      isCurrentMonth: isSameMonth(day, date),
      isToday: isSameDay(day, today),
      weekNumber: getISOWeek(day),
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
    });

    if (currentWeek.length === 7) {
      grid.push(currentWeek);
      currentWeek = [];
    }
  }

  // Ensure we have at least 5 rows, pad with next week if needed
  if (currentWeek.length > 0) {
    // Pad remaining days
    while (currentWeek.length < 7) {
      const lastDay = currentWeek[currentWeek.length - 1].date;
      const nextDay = addDays(lastDay, 1);
      currentWeek.push({
        date: nextDay,
        isCurrentMonth: false,
        isToday: isSameDay(nextDay, today),
        weekNumber: getISOWeek(nextDay),
        isWeekend: nextDay.getDay() === 0 || nextDay.getDay() === 6,
      });
    }
    grid.push(currentWeek);
  }

  return grid;
}

/**
 * Get the 7 days of the week containing the given date.
 */
export function getWeekDays(date: Date, firstDayOfWeek: number): Date[] {
  const weekStart = startOfWeek(date, { weekStartsOn: firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
  return eachDayOfInterval({
    start: weekStart,
    end: addDays(weekStart, 6),
  });
}

/**
 * Generate time slots for a day/week time grid.
 */
export function getTimeSlots(
  intervalMinutes: number,
  businessHoursStart: number,
  businessHoursEnd: number
): TimeSlot[] {
  const slots: TimeSlot[] = [];
  const totalMinutesInDay = 24 * 60;
  // Guard against infinite loop from invalid interval
  const safeInterval = Math.max(1, Math.floor(intervalMinutes));

  for (let mins = 0; mins < totalMinutesInDay; mins += safeInterval) {
    const hour = Math.floor(mins / 60);
    const minutes = mins % 60;
    const isPM = hour >= 12;
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const label = `${displayHour}:${String(minutes).padStart(2, '0')} ${isPM ? 'PM' : 'AM'}`;

    slots.push({
      hour,
      minutes,
      label,
      isBusinessHour: hour >= businessHoursStart && hour < businessHoursEnd,
    });
  }

  return slots;
}

/**
 * Check if an event is all-day (no time component or allDay flag).
 */
export function isAllDayEvent(event: { start: string; end?: string; allDay?: boolean }): boolean {
  if (event.allDay) return true;
  // Date-only strings have no 'T' separator
  if (!event.start.includes('T')) return true;
  return false;
}

/**
 * Check if an event spans multiple days.
 */
export function isMultiDayEvent(event: { start: string; end?: string }): boolean {
  if (!event.end) return false;
  const startDate = parseISO(event.start);
  const endDate = parseISO(event.end);
  if (!isValid(startDate) || !isValid(endDate)) return false;

  // Compare date parts only
  const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

  return startDay.getTime() !== endDay.getTime();
}

/**
 * Get all dates an event spans (start inclusive, end exclusive for multi-day).
 */
export function getEventDateRange(start: string, end?: string): Date[] {
  const startDate = parseISO(start);
  if (!isValid(startDate)) return [];

  if (!end) return [new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())];

  const endDate = parseISO(end);
  if (!isValid(endDate)) return [new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())];

  const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

  // If same day, return single date
  if (startDay.getTime() === endDay.getTime()) {
    return [startDay];
  }

  // Return all days from start to end (exclusive)
  const days: Date[] = [];
  let current = new Date(startDay);
  while (current < endDay) {
    days.push(new Date(current));
    current = addDays(current, 1);
  }

  return days;
}

/**
 * Navigate to next/previous period based on view mode.
 */
export function navigateDate(date: Date, viewMode: CalendarViewMode, direction: number): Date {
  switch (viewMode) {
    case 'month':
      return addMonths(date, direction);
    case 'week':
    case 'agenda':
      return addWeeks(date, direction);
    case 'day':
      return addDays(date, direction);
    default:
      return addMonths(date, direction);
  }
}

/**
 * Get the number of calendar days between two dates.
 */
export function daysBetween(start: Date, end: Date): number {
  return differenceInCalendarDays(end, start);
}
